// Reconstruct the `compareResult` shape the results view consumes from a saved
// run (header + flat detail rows returned by GET /schemaComparison/:id). The
// detail rows are the flattened audit format (one row per changed field), so
// the reconstructed diff shows changed columns + summary stats — it does not
// carry DDL or the full/unchanged column lists a live comparison has.

function statusFromChange(changeType) {
  if (changeType === 'ADD') return 'ADDED';
  if (changeType === 'REMOVE') return 'REMOVED';
  return 'MODIFIED';
}

function columnDiffType(changeType) {
  if (changeType === 'ADD') return 'COLUMN_ADDED';
  if (changeType === 'REMOVE') return 'COLUMN_REMOVED';
  return 'COLUMN_MODIFIED';
}

function parseStats(stats) {
  if (!stats) return {};
  if (typeof stats === 'string') {
    try {
      return JSON.parse(stats);
    } catch (e) {
      return {};
    }
  }
  return stats;
}

export function savedRunToCompareResult(saved) {
  if (!saved) return null;

  const objectsMap = new Map();
  const indexDiffs = [];
  const fkDiffs = [];

  const getObject = (name, type, status) => {
    if (!objectsMap.has(name)) {
      objectsMap.set(name, {objectName: name, objectType: type, status, columnDiffs: [], _colMap: new Map()});
    }
    return objectsMap.get(name);
  };

  for (const d of (saved.details || [])) {
    if (d.change_level === 'INDEX') {
      indexDiffs.push({key: d.object_name, status: statusFromChange(d.change_type)});
      continue;
    }
    if (d.change_level === 'FK') {
      fkDiffs.push({key: d.object_name, status: statusFromChange(d.change_type)});
      continue;
    }
    if (d.change_level === 'OBJECT') {
      const obj = getObject(d.object_name, d.object_type, statusFromChange(d.change_type));
      obj.status = statusFromChange(d.change_type);
      continue;
    }

    // COLUMN — column-level changes make the object MODIFIED.
    const obj = getObject(d.object_name, d.object_type, 'MODIFIED');
    const cdType = columnDiffType(d.change_type);
    let cd = obj._colMap.get(d.column_name);
    if (!cd) {
      cd = {type: cdType, columnName: d.column_name, fieldDiffs: []};
      if (cdType === 'COLUMN_ADDED') cd.new = {_label: d.new_value};
      if (cdType === 'COLUMN_REMOVED') cd.old = {_label: d.old_value};
      obj._colMap.set(d.column_name, cd);
      obj.columnDiffs.push(cd);
    }
    if (cdType === 'COLUMN_MODIFIED') {
      // Slice 2k: carry redactedFields THROUGH the transform. The backend attaches it to the
      // detail row, and this function reshapes those rows into fieldDiffs — so without this the
      // key dies here and the grid renders the '[redacted]' sentinel as if it were a real value.
      // Same trap getChangeTimeline has server-side, in the other direction.
      cd.fieldDiffs.push({field: d.field_changed, oldValue: d.old_value, newValue: d.new_value,
        redactedFields: d.redactedFields});
    }
  }

  const objects = [...objectsMap.values()].map(({_colMap, ...obj}) => obj);

  return {
    comparisonId: saved.comparison_id,
    engine: saved.engine,
    stats: parseStats(saved.stats),
    diff: objects,
    indexDiffs,
    fkDiffs,
    ddl: null,
    savedRun: true,
    createdAt: saved.created_at,
    // The saved header stores the display labels in source_schema/target_schema.
    sourceLabel: saved.source_schema,
    targetLabel: saved.target_schema,
    sourceType: saved.source_type,
    targetType: saved.target_type,
  };
}

export default savedRunToCompareResult;
